import React, { useState, useEffect } from 'react';

interface ConnectionTestStepProps {
  provider: string;
  model: string;
  endpoint: string;
  secretId: string;
  azureApiVersion?: string;
  region?: string;
  onNext: () => void;
  onBack: () => void;
  onCancel: () => void;
}

const ConnectionTestStep: React.FC<ConnectionTestStepProps> = ({
  provider,
  model,
  endpoint,
  secretId,
  azureApiVersion,
  region,
  onNext,
  onBack,
  onCancel
}) => {
  const [testing, setTesting] = useState(true);
  const [testResult, setTestResult] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Auto-start the test when component mounts
    handleTest();
  }, []);

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setErrorMessage('');

    try {
      // Call backend API to test LLM connection
      const response = await fetch('/api/v1/pipes/test-llm-connection', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          provider,
          endpoint,
          secret_id: secretId,
          azure_api_version: azureApiVersion,
          region,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      
      if (data.status === 'success') {
        setTestResult('success');
      } else {
        setTestResult('error');
        setErrorMessage(data.message || 'Connection test failed');
      }
    } catch (err: any) {
      setTestResult('error');
      setErrorMessage(err.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleContinue = () => {
    if (testResult === 'success') {
      onNext();
    } else {
      // Allow skipping if test failed but user wants to continue anyway
      if (window.confirm('Connection test failed. Do you want to continue anyway?')) {
        onNext();
      }
    }
  };

  const getProviderName = () => {
    const names: Record<string, string> = {
      'openai': 'OpenAI',
      'anthropic': 'Anthropic Claude',
      'bedrock': 'AWS Bedrock',
      'azure': 'Azure OpenAI',
      'gemini': 'Google Gemini'
    };
    return names[provider] || provider;
  };

  return (
    <div className="card shadow">
      <div className="card-body text-center py-5">
        <h4 className="mb-4">
          <i className="fas fa-plug mr-2"></i>
          Testing Connection
        </h4>

        {testing && (
          <div className="py-4">
            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
              <span className="sr-only">Testing...</span>
            </div>
            <p className="text-muted">Testing connection to {getProviderName()}...</p>
            <p className="text-muted small">Please wait while we verify your configuration...</p>
          </div>
        )}

        {testResult === 'success' && (
          <div>
            <div className="mb-4">
              <i className="fas fa-check-circle text-success" style={{ fontSize: '4rem' }}></i>
            </div>
            
            <h3 className="mb-3">Connection Test Successful!</h3>
            
            <p className="text-muted mb-4">
              Successfully connected to {getProviderName()}.
            </p>

            <div className="mt-4">
              <button
                type="button"
                className="btn btn-primary"
                onClick={onNext}
              >
                Continue
                <i className="fas fa-arrow-right ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {testResult === 'error' && (
          <div>
            <div className="mb-4">
              <i className="fas fa-exclamation-triangle text-danger" style={{ fontSize: '4rem' }}></i>
            </div>
            <div className="alert alert-danger d-inline-block">
              <strong>Connection Failed</strong>
              <p className="mb-0 mt-2">
                {errorMessage}
              </p>
            </div>
            <div className="mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={onBack}
              >
                <i className="fas fa-arrow-left mr-1"></i>
                Go Back
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConnectionTestStep;
